import { Loader2 } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useReactionSummaryPopover } from "@/hooks/useReactionSummaryPopover";
import type {
  Reaction,
  ReactionBreakdown,
  ReactionType,
} from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";
import { getReactionEmoji } from "@/lib/utils/reactionsUtils";

interface ReactionSummaryProps {
  contentId?: number | string | null;
  objectModel?: string;
  objectId?: number | string | null;
  total?: number;
  lastReactions?: Reaction[];
  reactionBreakdown?: ReactionBreakdown;
  currentUserEmotion?: ReactionType | null;
  className?: string;
  refreshKey?: number;
}

export const ReactionSummary: React.FC<ReactionSummaryProps> = ({
  contentId,
  objectModel,
  objectId,
  total = 0,
  reactionBreakdown,
  currentUserEmotion = null,
  className,
  refreshKey,
}) => {
  const { t } = useTranslation();
  const {
    open,
    chips,
    reactions,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    activeEmotionCount,
    activeEmotionLabel,
    openNow,
    scheduleClose,
    setActiveEmotion,
    handleOpenChange,
  } = useReactionSummaryPopover({
    contentId,
    objectModel,
    objectId,
    refreshKey,
    reactionBreakdown,
  });

  if (total === 0) {
    return null;
  }

  const summaryLabel =
    total > 0 ? `${t("common.reaction")} (${total})` : t("common.reaction");
  const popoverTitle = activeEmotionLabel
    ? activeEmotionCount === undefined
      ? activeEmotionLabel
      : `${activeEmotionLabel} (${activeEmotionCount})`
    : summaryLabel;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex flex-wrap items-center gap-1 bg-transparent p-0 text-xs leading-none text-muted-foreground outline-none focus:outline-none focus-visible:rounded-sm focus-visible:ring-1 focus-visible:ring-rw-main/40",
            className,
          )}
          aria-label={summaryLabel}
          onMouseEnter={() => {
            if (chips.length === 0) {
              setActiveEmotion(undefined);
              openNow();
            }
          }}
          onMouseLeave={scheduleClose}
        >
          {chips.map(({ type, count }) => (
            <span
              key={type}
              onMouseEnter={() => {
                setActiveEmotion(type);
                openNow();
              }}
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5",
                type === currentUserEmotion
                  ? "bg-rw-main text-white"
                  : "bg-gray-100 text-foreground",
              )}
            >
              <span className="flex size-3.5 items-center justify-center">
                {getReactionEmoji(type, "h-3.5 w-3.5")}
              </span>
              <span className="font-medium leading-none">{count}</span>
            </span>
          ))}
          {chips.length === 0 && (
            <span className="font-medium leading-none text-foreground">
              {total}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-64 p-0"
        side="top"
        align="start"
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
      >
        <div className="border-b px-3 py-2 text-sm font-medium text-foreground">
          {popoverTitle}
        </div>
        <div className="max-h-64 overflow-y-auto">
          {isLoading && reactions.length === 0 ? (
            <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t("common.loading", { defaultValue: "Loading" })}
            </div>
          ) : reactions.length === 0 ? (
            <div className="px-3 py-4 text-xs text-muted-foreground">
              {t("common.noReactions", { defaultValue: "No reactions yet" })}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {reactions.map((reaction) => (
                <li
                  key={reaction.id}
                  className="flex items-center gap-3 px-3 py-2"
                >
                  <span className="flex size-6 shrink-0 items-center justify-center">
                    {getReactionEmoji(reaction.emotion, "h-5 w-5")}
                  </span>
                  <Avatar className="size-7 shrink-0">
                    <AvatarImage
                      src={reaction.createdBy?.image_url || ""}
                      alt={reaction.createdBy?.display_name}
                    />
                    <AvatarFallback className="text-xs">
                      {reaction.createdBy?.display_name?.[0] || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-1 flex-col">
                    <span className="line-clamp-1 text-xs font-medium text-foreground">
                      {reaction.createdBy?.display_name || "Unknown"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {hasNextPage && (
          <div className="border-t px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage
                ? t("common.loading", { defaultValue: "Loading" })
                : t("common.loadMore", { defaultValue: "Load more" })}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
